import { Avatar, AvatarGroup, Box, Skeleton, Typography } from '@mui/material'
import { TypographyVariant } from '@mui/material/styles/createTypography'
import { C1Tooltip } from 'c1ui/components/C1Tooltip'
import { AppUserAvatar } from 'components/core/avatar'
import { AvatarSize, getAvatarSizeStyles } from 'components/core/avatar/AvatarSizes'
import { avatarStyles } from 'components/core/avatar/styles'
import { C1DisplayNameSkeleton } from 'components/core/display-name-details'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { MinimalAPIError } from 'components/core/errors/api'
import { AppUserDisplayName } from 'components/pages/app/users/app-user-display-name'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { AppUserServiceSearchRequest } from 'pbts/c1/api/app/v1/app_user'
import { FC, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { searchAppUsersApi } from 'redux_toolkit/app-users'

export type C1MultiAppUserDisplayProps = {
    appUsers: AppUserValue[]
    readOnly?: boolean
    maxWidth?: string
    padded?: boolean
    margin?: boolean
    maxToShow?: number
    avatarSize?: AvatarSize
    typographyVariant?: TypographyVariant
    showTooltip?: boolean
    tooltipTitle?: string
}

export const C1MultiAppUserDisplaySkeleton: FC = () => (
    <AvatarGroup>
        {[1, 2, 3].map((v) => (
            <Avatar key={v} sx={{ ...avatarStyles.avatar, ...getAvatarSizeStyles('md'), backgroundColor: 'grey.50' }}>
                <Skeleton variant="circular" sx={{ ...getAvatarSizeStyles('md') }} />
            </Avatar>
        ))}
    </AvatarGroup>
)

export type C1MultiAppUserDisplayWithFetchProps = Omit<C1MultiAppUserDisplayProps, 'appUsers'> & {
    userIds: string[]
    appId: string
}

export const C1MultiAppUserDisplayWithFetch: FC<C1MultiAppUserDisplayWithFetchProps> = ({
    userIds,
    appId,
    ...rest
}) => {
    const request = useMemo(() => AppUserServiceSearchRequest.create({
        appUserIds: userIds,
        appId,
        expandMask: { paths: ['*'] },
    }), [userIds, appId])

    const { data, isLoading, isError, error } = searchAppUsersApi.useSearch(request)

    if (userIds.length === 0) return <C1EmptyIndicator />

    if (isLoading) {
        return userIds.length > 1
            ? <C1MultiAppUserDisplaySkeleton />
            : <C1DisplayNameSkeleton />
    }

    if ((isError && error) || !data) {
        const err = <FormattedMessage id="component_api_error_app_users" defaultMessage="Error fetching accounts" />
        return <MinimalAPIError err={err} />
    }

    return <C1MultiAppUserDisplay appUsers={data.flat()} {...rest} />
}

export const C1MultiAppUserDisplay: FC<C1MultiAppUserDisplayProps> = ({
    appUsers,
    readOnly,
    maxWidth: _maxWidth,
    padded = false,
    margin = false,
    maxToShow = 4,
    avatarSize = 'md',
    typographyVariant = 'body1',
    showTooltip = false,
    tooltipTitle,
}) => {
    const group = useMemo(() => (
        <AvatarGroup
            slotProps={{
                additionalAvatar: {
                    sx: {
                        ...avatarStyles.avatar,
                        ...getAvatarSizeStyles(avatarSize),
                        marginLeft: '0px',
                        transform: 'translateX(12px)',
                        background: 'transparent',
                        '&.MuiAvatarGroup-avatar': {
                            borderColor: 'transparent',
                        },
                    },
                },
            }}
            max={maxToShow}
            component="div"
            spacing="small"
            tabIndex={0}
            sx={{
                marginLeft: 3,
                display: 'inline-flex',
                '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: 2,
                    borderRadius: 1,
                },
            }}
        >
            {appUsers.map((appUser, i) => appUser && (
                <AppUserAvatar
                    key={`${i}-${appUser.id}`}
                    displayName={appUser.displayName}
                    size={avatarSize}
                    appUserType={appUser.appUserType}
                    email={appUser.email}
                    decorative
                />
            ))}
        </AvatarGroup>
    ), [maxToShow, appUsers, avatarSize])

    if (appUsers.length === 0) return <C1EmptyIndicator />

    if (appUsers.length === 1) {
        return (
            <AppUserDisplayName
                appUser={appUsers[0]}
                avatarSize={avatarSize}
                margin={margin}
                padded={padded}
                displayNameVariant={typographyVariant}
                readOnly={readOnly}
                includeDetails={false}
                showTooltip={showTooltip}
            />
        )
    }

    return (
        <C1Tooltip
            placement="right-end"
            title={
                <C1MultiAppUserDisplayToolTip
                    appUsers={appUsers}
                    showTooltip={showTooltip}
                    readOnly={readOnly}
                    tooltipTitle={tooltipTitle}
                />
            }
        >
            {group}
        </C1Tooltip>
    )
}

export type C1MultiAppUserDisplayToolTipProps = {
    appUsers: AppUserValue[]
    showTooltip?: boolean
    readOnly?: boolean
    tooltipTitle?: string
}

export const C1MultiAppUserDisplayToolTip: FC<C1MultiAppUserDisplayToolTipProps> = ({
    appUsers,
    showTooltip = true,
    readOnly = false,
    tooltipTitle,
}) => {
    if (!appUsers || appUsers.length === 0) return null

    const visible = appUsers.slice(0, 14)
    const removed = appUsers.slice(14).length

    return (
        <Box margin={1}>
            <Typography mb={2} variant="subtitle1" component="div">
                {tooltipTitle || <FormattedMessage id="components_multi_app_user_popup_heading" defaultMessage="Accounts" />}
            </Typography>
            {visible.map((appUser) => (
                <Box key={appUser?.id} marginBottom={2}>
                    <AppUserDisplayName
                        appUser={appUser}
                        includeDetails={false}
                        showTooltip={showTooltip}
                        readOnly={readOnly}
                        margin={false}
                    />
                </Box>
            ))}

            {removed > 0 && (
                <Typography variant="caption" color="info.contrastText">
                    <FormattedMessage
                        id="components_multi_app_user_popup_more"
                        defaultMessage="+ {removed, plural, one {# other} other {# others}}"
                        values={{ removed }}
                    />
                </Typography>
            )}
        </Box>
    )
}
